import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, ExternalLink, Video, Calendar, MapPin } from 'lucide-react';
import { formatDate } from '../../utils/formatters';
import { EVENT_TYPES } from '../../utils/constants';
import { deleteEvent } from '../../hooks/useEvents';

function EventCard({ event }) {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async (e) => {
    e.stopPropagation();

    if (!window.confirm(`Are you sure you want to delete "${event.eventName}"? This will also delete all associated videos.`)) {
      return;
    }

    setIsDeleting(true);
    const result = await deleteEvent(event.id);

    if (!result.success) {
      alert(`Error deleting event: ${result.error}`);
      setIsDeleting(false);
    }
    // If successful, the real-time listener will update the UI automatically
  };

  const handleCardClick = () => {
    navigate(`/admin/events/${event.id}`);
  };

  const getEventTypeLabel = () => {
    const type = EVENT_TYPES.find(t => t.value === event.eventType);
    return type ? type.label : event.eventType;
  };

  // Format the event date from Firestore Timestamp
  const eventDateFormatted = event.eventDate?.toDate
    ? formatDate(event.eventDate.toDate())
    : formatDate(new Date(event.eventDate));

  return (
    <div
      onClick={handleCardClick}
      className="event-card cursor-pointer group relative"
    >
      {/* Status Indicator - Top Right */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <span className={`w-2.5 h-2.5 rounded-full ${
          event.isPublic
            ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]'
            : 'bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.5)]'
        }`} />
        <span className={`text-xs uppercase tracking-wider font-medium ${
          event.isPublic ? 'text-emerald-300' : 'text-blue-300'
        }`}>
          {event.isPublic ? 'Public' : 'Private'}
        </span>
      </div>

      

      {/* Event Title */}
      <h3 className="text-2xl font-bold text-white mb-4 pr-28 group-hover:text-gold transition-colors duration-300">
        {event.eventName}
      </h3>

      {/* Metadata - Muted */}
      <div className="space-y-2 mb-5">
        <div className="flex items-center gap-2 text-sm text-neutral-400">
          <Calendar className="w-4 h-4 text-neutral-500" />
          <span>{eventDateFormatted}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-neutral-400">
          <MapPin className="w-4 h-4 text-neutral-500" />
          <span>{event.location}</span>
        </div>
      </div>

      {/* Event Type Badge & Upload Count */}
      <div className="flex items-center justify-between mb-6">
        <span className="px-3 py-1.5 bg-gold/10 text-gold rounded-full text-xs font-semibold uppercase tracking-wide">
          {getEventTypeLabel()}
        </span>
        <div className="flex items-center gap-2 text-neutral-300">
          <Video className="w-4 h-4 text-gold" />
          <span className="font-semibold">{event.videoCount || 0}</span>
          <span className="text-sm text-neutral-500">upload{event.videoCount !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Action Buttons - Properly Spaced */}
      <div className="mt-auto flex gap-3 pt-5 border-t border-white/10">
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/kiosk/${event.id}`);
          }}
          className="flex-1 min-h-[52px] flex items-center justify-center gap-2 px-4 py-3 bg-transparent border-2 border-gold text-gold hover:bg-gold/10 rounded-xl transition-all duration-200 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/50 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900"
        >
          <ExternalLink className="w-4 h-4" />
          Open Kiosk
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/gallery/${event.id}`);
          }}
          className="flex-1 min-h-[52px] flex items-center justify-center gap-2 px-4 py-3 bg-[#D4AF37] hover:bg-[#E5C04B] text-black rounded-xl transition-all duration-200 text-sm font-semibold shadow-lg hover:shadow-[0_4px_20px_rgba(212,175,55,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900"
        >
          <Video className="w-4 h-4" />
          View Gallery
        </button>
      </div>
    </div>
  );
}

export default EventCard;
